import { createContext, useContext, useMemo, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import {
  AccountType,
  IAccountListResponse,
  IApiRequest,
  IApiResponse,
  IApiToastMessageConfig,
  IAreaTypeResponse,
  IBidOptionResponse,
  IBidResponse,
  ICategoryFilterResponse,
  ICommunitiesResponse,
  IFloorResponse,
  IMaterialsResponse,
  ISeriesGroupResponse,
  PlanStatus,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useAccounts,
  useAreaTypes,
  useBidOptions,
  useBidRequests,
  useCategories,
  useCommunities,
  useCustomerMaterials,
  useFloors,
  useSeriesGroup,
} from "@mibuildweb/hooks";
import { schema } from "./schema/schema";
import { CreateBidRequestApiPayload } from "../CreateBidRequest/CreateBidRequest.type";
import { CreateBidPayload } from "./CreateBids.types";
import { STEP } from "./constants";
import { useCreateBidViewModel } from "./useCreateBidViewModel";

const CreateBidContext = createContext<CreateBidContextType | undefined>(
  undefined
);

export function useCreateBidContext() {
  const context = useContext(CreateBidContext);
  if (!context)
    throw new Error(
      "useCreateBidContext must be used within CreateBidProvider"
    );
  return context;
}

export const DEFAULT_VALUES = {
  id: createUUID(),
  name: "",
  communityId: null,
  builderId: "",
  additionalFeatures: "",
  pipeBrand: "",
  documents: [],
  planAreas: [],
  options: [],
  bidRequestId: null,
  bidRequestIndex: null,
  bidRequest: null,
  assigneeId: null,
  seriesId: null,
  seriesGroupId: null,
  requirementIds: [],
};

export function CreateBidProvider({
  children,
}: Readonly<CreateBidProviderProps>) {
  const [activeStep, setActiveStep] = useState(STEP.START);
  const [startOption, setStartOption] = useState("");
  const [isConfigStepValid, setIsConfigStepValid] = useState(false);
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);

  const form = useForm<CreateBidPayload>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(schema),
  });

  const {
    isLoading: isBidLoading,
    bidDetails,
    fetchBidDetails,
    handleUpdateBid,
    handleUpdateBidStatus,
    handleUpdatePlanRequirements,
  } = useCreateBidViewModel({ reset: form.reset });
  const {
    isLoading: isCommunitiesLoading,
    communities,
    getCommunities,
  } = useCommunities();
  const {
    isLoading: isAccountsLoading,
    accounts,
    getAccounts,
    accountDetails,
    getAccountDetails,
  } = useAccounts({ accountTypes: [AccountType.Builder] });
  const { isLoading: isFloorsLoading, floors, getFloors } = useFloors();
  const {
    isLoading: isCategoriesLoading,
    getCategoryFilters,
    categoryFilterOptions,
  } = useCategories();
  const {
    isLoading: isAreaTypesLoading,
    areaTypes,
    getAreaTypes,
  } = useAreaTypes();
  const { isLoading: isCustomerMaterialsLoading, getCustomerMaterials } =
    useCustomerMaterials();
  const {
    isLoading: isBidOptionsLoading,
    getOption,
    createOption,
    updateOption,
    deleteOption,
  } = useBidOptions();
  const {
    isLoading: isBidRequestsLoading,
    getBidRequests,
    createBidRequest,
  } = useBidRequests();
  const {
    isLoading: isSeriesGroupsLoading,
    seriesGroups,
    getSeriesGroups,
  } = useSeriesGroup();

  const isPageLoading =
    isBidLoading ||
    isCommunitiesLoading ||
    isAccountsLoading ||
    isFloorsLoading ||
    isAreaTypesLoading ||
    isCategoriesLoading ||
    isCustomerMaterialsLoading ||
    isBidOptionsLoading ||
    isBidRequestsLoading ||
    isSeriesGroupsLoading;

  const value = useMemo(
    () => ({
      isLoading: isPageLoading,
      form,
      activeStep,
      bidDetails,
      fetchBidDetails,
      setActiveStep,
      handleUpdateBid,
      handleUpdateBidStatus,
      handleUpdatePlanRequirements,
      communities,
      getCommunities,
      accounts,
      getAccounts,
      getBidRequests,
      createBidRequest,
      isConfigStepValid,
      setIsConfigStepValid,
      startOption,
      setStartOption,
      openDocumentViewer,
      setOpenDocumentViewer,
      floors,
      getFloors,
      areaTypes,
      getAreaTypes,
      categoryFilterOptions,
      getCategoryFilters,
      getCustomerMaterials,
      accountDetails,
      getAccountDetails,
      seriesGroups,
      getSeriesGroups,
      getOption,
      createOption,
      updateOption,
      deleteOption,
    }),
    [form.formState, activeStep, bidDetails, isConfigStepValid, isPageLoading]
  );

  return (
    <CreateBidContext.Provider value={value}>
      {children}
    </CreateBidContext.Provider>
  );
}

interface CreateBidProviderProps {
  children: React.ReactNode;
}

interface CreateBidContextType {
  isLoading: boolean;
  form: UseFormReturn<CreateBidPayload>;
  activeStep: number;
  bidDetails: IBidResponse | null;
  fetchBidDetails: (bidId?: string) => void;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  handleUpdateBid: (
    data: CreateBidPayload,
    successText?: string
  ) => Promise<boolean>;
  handleUpdateBidStatus: (
    bid: CreateBidPayload,
    status: PlanStatus
  ) => Promise<boolean>;
  handleUpdatePlanRequirements: ReturnType<
    typeof useCreateBidViewModel
  >["handleUpdatePlanRequirements"];
  communities: ICommunitiesResponse[];
  getCommunities: () => void;
  accounts: IAccountListResponse[];
  getAccounts: () => void;
  getBidRequests: (request: IApiRequest) => IApiResponse<IBidResponse[]>;
  createBidRequest: (
    data: CreateBidRequestApiPayload,
    successText?: string
  ) => Promise<boolean>;
  isConfigStepValid: boolean;
  setIsConfigStepValid: React.Dispatch<React.SetStateAction<boolean>>;
  startOption: string;
  setStartOption: React.Dispatch<React.SetStateAction<string>>;
  openDocumentViewer: boolean;
  setOpenDocumentViewer: React.Dispatch<React.SetStateAction<boolean>>;
  floors: IFloorResponse[];
  getFloors: () => void;
  areaTypes: IAreaTypeResponse[];
  getAreaTypes: () => void;
  categoryFilterOptions: ICategoryFilterResponse[];
  getCategoryFilters: () => void;
  getCustomerMaterials: (
    request: IApiRequest & { accountId: string }
  ) => IApiResponse<IMaterialsResponse[]>;
  accountDetails: IAccountListResponse | null;
  getAccountDetails: (id: string) => void;
  seriesGroups: ISeriesGroupResponse[];
  getSeriesGroups: (request?: IApiRequest) => void;
  getOption: (request: {
    planId: string;
    optionId: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => IApiResponse<IBidOptionResponse>;
  createOption: (request: {
    planId: string;
    data: Partial<IBidOptionResponse>;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => IApiResponse<IBidOptionResponse>;
  updateOption: (request: {
    planId: string;
    data: Partial<IBidOptionResponse>;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => IApiResponse<IBidOptionResponse>;
  deleteOption: (request: {
    planId: string;
    id: string;
    modifiedOn?: string | null;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => IApiResponse<IBidOptionResponse>;
}
